import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { FormSummaryMessage } from '@/components/ui/FormFeedback';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import {
  ApiRequestError,
  fetchSystemUserInfo,
  getUserPostalCodes,
  postAddServicerPostalCode,
  postRemoveServicerPostalCode,
} from '@/api';
import {
  SERVICE_AREA_CATALOG,
  areasForCity,
  findServiceArea,
  formatServiceAreaLabel,
} from '@/constants/serviceAreas';

const selectClass =
  'w-full h-8 px-2.5 text-xs font-normal rounded-md border border-gray-200 bg-white outline-none focus:border-green-500 cursor-pointer disabled:bg-gray-50 disabled:text-gray-400';

export default function ServiceAreasPage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [codes, setCodes] = useState<string[]>(user?.postalCodes ?? []);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [formError, setFormError] = useState('');
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [countryCode, setCountryCode] = useState(SERVICE_AREA_CATALOG[0].code);
  const [regionCode, setRegionCode] = useState(SERVICE_AREA_CATALOG[0].regions[0].code);
  const [cityCode, setCityCode] = useState(SERVICE_AREA_CATALOG[0].regions[0].cities[0].code);
  const locatedRef = useRef(false);

  const country = SERVICE_AREA_CATALOG.find((item) => item.code === countryCode) ?? SERVICE_AREA_CATALOG[0];
  const region = country.regions.find((item) => item.code === regionCode) ?? country.regions[0];
  const city = region.cities.find((item) => item.code === cityCode) ?? region.cities[0];
  const cityAreas = useMemo(
    () => areasForCity(country.code, region.code, city.code),
    [country.code, region.code, city.code],
  );
  const selectedCount = cityAreas.filter((area) => codes.includes(area.postalCode)).length;
  const unknownCodes = codes.filter((code) => !findServiceArea(code));

  const applyCodes = useCallback((next: string[]) => {
    setCodes(next);
    updateUser?.({ postalCodes: next });
  }, [updateUser]);

  const loadCodes = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const info = await fetchSystemUserInfo();
      applyCodes(getUserPostalCodes(info));
    } catch (err) {
      setLoadError(err instanceof ApiRequestError ? err.message : 'Failed to load service areas.');
    } finally {
      setLoading(false);
    }
  }, [applyCodes]);

  useEffect(() => {
    void loadCodes();
  }, [loadCodes]);

  useEffect(() => {
    if (locatedRef.current || codes.length === 0) return;
    const hit = codes.map((code) => findServiceArea(code)).find(Boolean);
    if (!hit) return;
    locatedRef.current = true;
    setCountryCode(hit.country.code);
    setRegionCode(hit.region.code);
    setCityCode(hit.city.code);
  }, [codes]);

  const toggleArea = async (postalCode: string) => {
    if (busyCode) return;
    const selected = codes.includes(postalCode);
    setFormError('');
    setBusyCode(postalCode);
    try {
      const result = selected
        ? await postRemoveServicerPostalCode(postalCode)
        : await postAddServicerPostalCode(postalCode);
      applyCodes(result.postalCodes);
    } catch (err) {
      setFormError(
        err instanceof ApiRequestError
          ? err.message
          : selected
            ? 'Failed to remove this service area.'
            : 'Failed to add this service area.',
      );
    } finally {
      setBusyCode(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SubpageHeader
        title="Service Areas"
        subtitle="Choose the areas you cover"
        onBack={() => navigate('/me/profile')}
      />

      <div className="px-4 pt-3 space-y-2.5 pb-[calc(2rem+env(safe-area-inset-bottom))]">
        <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2.5">
          <label className="block" htmlFor="service-area-country">
            <span className="block text-xs font-normal text-gray-700 mb-1">Country</span>
            <select
              id="service-area-country"
              className={selectClass}
              value={country.code}
              disabled={loading || !!busyCode}
              onChange={(event) => {
                const next = SERVICE_AREA_CATALOG.find((item) => item.code === event.target.value);
                if (!next) return;
                setCountryCode(next.code);
                setRegionCode(next.regions[0].code);
                setCityCode(next.regions[0].cities[0].code);
                setMenuOpen(false);
              }}
            >
              {SERVICE_AREA_CATALOG.map((item) => (
                <option key={item.code} value={item.code}>{item.name}</option>
              ))}
            </select>
          </label>

          <label className="block" htmlFor="service-area-region">
            <span className="block text-xs font-normal text-gray-700 mb-1">Region</span>
            <select
              id="service-area-region"
              className={selectClass}
              value={region.code}
              disabled={loading || !!busyCode}
              onChange={(event) => {
                const next = country.regions.find((item) => item.code === event.target.value);
                if (!next) return;
                setRegionCode(next.code);
                setCityCode(next.cities[0].code);
                setMenuOpen(false);
              }}
            >
              {country.regions.map((item) => (
                <option key={item.code} value={item.code}>{item.name}</option>
              ))}
            </select>
          </label>

          <label className="block" htmlFor="service-area-city">
            <span className="block text-xs font-normal text-gray-700 mb-1">City</span>
            <select
              id="service-area-city"
              className={selectClass}
              value={city.code}
              disabled={loading || !!busyCode}
              onChange={(event) => {
                setCityCode(event.target.value);
                setMenuOpen(false);
              }}
            >
              {region.cities.map((item) => (
                <option key={item.code} value={item.code}>{item.name}</option>
              ))}
            </select>
          </label>

          <div>
            <span id="service-area-multi-label" className="block text-xs font-normal text-gray-700 mb-1">
              Service areas
            </span>
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={menuOpen}
              aria-labelledby="service-area-multi-label"
              disabled={loading}
              onClick={() => setMenuOpen((open) => !open)}
              className={`${selectClass} flex items-center justify-between gap-2 text-left`}
            >
              <span className={selectedCount > 0 ? 'text-gray-800' : 'text-gray-400'}>
                {selectedCount > 0 ? `${selectedCount} selected in ${city.name}` : `Select areas in ${city.name}`}
              </span>
              <i className={`ri-arrow-down-s-line text-gray-400 text-base ${menuOpen ? 'rotate-180' : ''}`} />
            </button>
            {menuOpen ? (
              <div
                role="listbox"
                aria-labelledby="service-area-multi-label"
                aria-multiselectable="true"
                className="mt-1 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white"
              >
                {cityAreas.map((area) => {
                  const selected = codes.includes(area.postalCode);
                  const busy = busyCode === area.postalCode;
                  return (
                    <label
                      key={area.postalCode}
                      role="option"
                      aria-selected={selected}
                      className="flex items-center gap-2.5 px-2.5 py-2 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        className="accent-green-600"
                        checked={selected}
                        disabled={!!busyCode}
                        onChange={() => void toggleArea(area.postalCode)}
                      />
                      <span className="flex-1 min-w-0 text-xs text-gray-800">{area.name}</span>
                      <span className="text-[11px] text-gray-400">{area.postalCode}</span>
                      {busy ? <i className="ri-loader-4-line animate-spin text-gray-400 text-sm" /> : null}
                    </label>
                  );
                })}
              </div>
            ) : null}
            <p className="text-gray-400 text-[11px] mt-1 leading-snug">
              Select every area you cover. You can choose areas in more than one city.
            </p>
          </div>
        </div>

        {loadError ? (
          <div className="space-y-2">
            <FormSummaryMessage message={loadError} />
            <button
              type="button"
              onClick={() => void loadCodes()}
              className="h-8 px-3 rounded-md border border-gray-200 bg-white text-xs text-gray-700 cursor-pointer hover:bg-gray-50"
            >
              Try again
            </button>
          </div>
        ) : null}

        <FormSummaryMessage message={formError} />

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
            <span className="text-gray-400 text-[10px] font-medium uppercase tracking-wider">
              Selected
            </span>
            {!loading ? <span className="text-gray-400 text-[10px]">{codes.length}</span> : null}
          </div>

          {loading ? (
            <div className="px-3 py-6 flex items-center justify-center text-gray-400 text-xs gap-1.5">
              <i className="ri-loader-4-line animate-spin" />
              Loading service areas…
            </div>
          ) : codes.length === 0 ? (
            <div className="px-3 py-6 text-center">
              <div className="w-9 h-9 mx-auto rounded-lg bg-gray-100 flex items-center justify-center mb-2">
                <i className="ri-map-pin-line text-gray-400" />
              </div>
              <div className="text-gray-700 text-xs font-medium">No service areas yet</div>
              <div className="text-gray-400 text-[11px] mt-1">
                Open the list above and select the areas you cover.
              </div>
            </div>
          ) : (
            codes.map((code, index) => {
              const hit = findServiceArea(code);
              return (
                <div
                  key={code}
                  className={`px-3 py-2 flex items-center gap-2.5 ${
                    index < codes.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <div className="w-7 h-7 flex items-center justify-center rounded-md bg-green-50 flex-shrink-0">
                    <i className="ri-map-pin-2-line text-green-600 text-sm" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-800 text-xs font-medium truncate">
                      {hit ? hit.area.name : code}
                    </div>
                    <div className="text-gray-400 text-[11px] truncate">
                      {hit ? `${hit.city.name}, ${hit.region.name} · ${hit.area.postalCode}` : formatServiceAreaLabel(code)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void toggleArea(code)}
                    disabled={!!busyCode}
                    aria-label={`Remove ${formatServiceAreaLabel(code)}`}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer disabled:opacity-60"
                  >
                    {busyCode === code ? (
                      <i className="ri-loader-4-line animate-spin text-sm" />
                    ) : (
                      <i className="ri-close-line text-sm" />
                    )}
                  </button>
                </div>
              );
            })
          )}
        </div>

        {unknownCodes.length > 0 ? (
          <p className="text-gray-400 text-[11px] leading-snug px-1">
            Some saved codes are outside the current area list. You can still remove them here.
          </p>
        ) : null}
      </div>
    </div>
  );
}
